import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout, { Product as V } from '../components/DashboardLayout';
import api from '../utils/api';
import { toast } from 'sonner';
import { CreditCard, Building2, User, Hash, ShieldCheck, AlertCircle, ArrowLeft, CheckCircle, Lock, Clock, XCircle, Mail, Phone } from 'lucide-react';

const SA_BANKS = [
  { name: 'ABSA Bank',                  code: '632005' },
  { name: 'African Bank',               code: '430000' },
  { name: 'Bidvest Bank',               code: '462005' },
  { name: 'Capitec Bank',               code: '470010' },
  { name: 'Discovery Bank',             code: '679000' },
  { name: 'First National Bank (FNB)',  code: '250655' },
  { name: 'Investec Bank',              code: '580105' },
  { name: 'Nedbank',                    code: '198765' },
  { name: 'Standard Bank',             code: '051001' },
  { name: 'TymeBank',                   code: '678910' },
  { name: 'Other',                      code: '' },
];

function SectionHead({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
      <span style={{
        fontSize: 10, fontWeight: 700, color: V.sub,
        textTransform: 'uppercase', letterSpacing: '0.12em',
        fontFamily: V.mono, whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: V.border }} />
    </div>
  );
}

function Field({ label, icon: Icon, children, hint }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 11, fontWeight: 700, color: V.sub,
        fontFamily: V.mono, textTransform: 'uppercase', letterSpacing: '0.1em',
        marginBottom: 8,
      }}>
        {Icon && <Icon size={12} />} {label}
      </label>
      {children}
      {hint && <p style={{ fontSize: 11, color: V.dim, marginTop: 4, fontFamily: V.mono }}>{hint}</p>}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 4,
  border: `1px solid ${V.border}`, background: V.hover,
  color: V.text, fontFamily: V.sans, fontSize: 13, outline: 'none',
  boxSizing: 'border-box',
};

function DetailRow({ icon: Icon, label, value, mono }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '12px 0', borderBottom: `1px solid ${V.border}`,
    }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: V.sub, fontSize: 13 }}>
        {Icon && <Icon size={14} />} {label}
      </span>
      <span style={{ fontWeight: 600, color: V.text, fontSize: 13, fontFamily: mono ? V.mono : V.sans }}>
        {value}
      </span>
    </div>
  );
}

function formatActivatesAt(isoString) {
  if (!isoString) return '';
  try {
    return new Date(isoString).toLocaleString('en-ZA', {
      day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    });
  } catch {
    return isoString;
  }
}

// FINAL BANKING STATUS, FEE & CHAT CLEANUP (2026-10-14): the badge used to
// collapse everything that wasn't exact-digit "verified" into one vague
// "Confirmation Pending" state — including sellers whose banking was
// genuinely attached and usable, just not provably digit-for-digit (which
// TradeSafe's masking makes the common case, not the exception). A seller
// who had already been paid out could still see "pending" forever. This
// now distinguishes three real states from banking_provider_verified/
// banking_provider_attached (both already returned by GET
// /users/banking-details/status — no backend change needed for this part):
//   - verified:  exact digits confirmed — the strongest claim.
//   - attached:  provider confirms a valid, usable token; digits are masked
//     so an exact match can't be proven. Still shown as a GOOD state — this
//     is the realistic "payouts work" signal, never framed as "pending".
//   - action required: provider genuinely could not attach/confirm this
//     banking — the only state that should prompt the seller to act.
function getBankingStatusPresentation(bankingStatus) {
  if (bankingStatus?.banking_provider_verified) {
    return {
      tone: 'success',
      title: 'Banking Details Verified',
      message: 'Your payout account is set up and confirmed with our payment processor.',
    };
  }
  if (bankingStatus?.banking_provider_attached) {
    // Section 3 fix (PR #77 review, 2026-10-15): "attached" (TradeSafe
    // accepted a bank account onto the token) is NOT the same claim as
    // "payouts are usable" — that also depends on the token's stored
    // identity still matching this profile and its payout interval still
    // being IMMEDIATE, neither of which "attached" proves on its own. Only
    // say payouts are confirmed usable when banking_payout_identity_matched
    // (set by "Refresh status", using the same safeguards the real payout
    // path enforces) is explicitly true; otherwise describe only what's
    // actually known, without implying more.
    if (bankingStatus?.banking_payout_identity_matched === true) {
      return {
        tone: 'success',
        title: 'Banking Details Confirmed',
        message: "Your payout account is active and usable for payouts. Our payment processor masks the exact account digits for security, so we can't display a digit-for-digit match — this doesn't affect your ability to receive payouts.",
      };
    }
    if (bankingStatus?.banking_payout_identity_matched === false) {
      // A real, confirmed mismatch (wrong identity on the token, or a
      // non-IMMEDIATE payout interval) — unlike "never checked yet", this
      // is a known issue, so it gets the warn tone rather than success.
      return {
        tone: 'warn',
        title: 'Action Required — Payout Settings Need Attention',
        message: "Your bank account is attached to your payout profile, but our last check found it doesn't fully match your current payout settings. Press \"Refresh status\" to check again, or contact support if this continues.",
      };
    }
    return {
      tone: 'success',
      title: 'Banking Details Confirmed',
      message: "Your bank account is attached to your payout profile. Our payment processor masks the exact account digits for security, so we can't display a digit-for-digit match. Press \"Refresh status\" to confirm it's fully ready for payouts.",
    };
  }
  return {
    tone: 'warn',
    title: 'Action Required — Banking Details Not Yet Confirmed',
    message: bankingStatus?.banking_provider_check_detail
      || "We couldn't confirm these details with our payment processor. Double-check your banking details are correct, or use \"Refresh status\" below — contact support if this continues.",
  };
}

function emptyBankingForm(userName = '') {
  return {
    bank_name: '', account_holder: userName, account_number: '',
    branch_code: '', account_type: 'savings', id_number: '',
  };
}

function BankingSettings() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bankingStatus, setBankingStatus] = useState(null);
  const [savedBankingDetails, setSavedBankingDetails] = useState(null);
  const [refreshingStatus, setRefreshingStatus] = useState(false);

  // ID-reconfirmation state (Section 4, PR #76's confirm-id-number endpoint)
  const [showIdForm, setShowIdForm] = useState(false);
  const [idNumberInput, setIdNumberInput] = useState('');
  const [confirmingId, setConfirmingId] = useState(false);

  // Initial setup flow state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bankingDetails, setBankingDetails] = useState(emptyBankingForm());

  // Change flow state
  // changeStep: null | 'form' | 'verify' | 'cooling'
  const [changeStep, setChangeStep] = useState(null);
  const [changeDetails, setChangeDetails] = useState(emptyBankingForm());
  const [changeRequestId, setChangeRequestId] = useState(null);
  const [changeOtpChannels, setChangeOtpChannels] = useState({ email: null, sms: null });
  const [otpValue, setOtpValue] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [pendingChange, setPendingChange] = useState(null); // from server status endpoint
  const [resending, setResending] = useState(false);

  const navigate = useNavigate();

  const fetchUserData = useCallback(async () => {
    try {
      const userRes = await api.get('/auth/me');
      setUser(userRes.data);
      const statusRes = await api.get('/users/banking-details/status');
      setBankingStatus(statusRes.data);
      if (statusRes.data.banking_details_completed && userRes.data.banking_details) {
        setSavedBankingDetails(userRes.data.banking_details);
      }
      setBankingDetails(emptyBankingForm(userRes.data.name));
      setChangeDetails(emptyBankingForm(userRes.data.name));

      // Check for any pending change request
      if (statusRes.data.banking_details_completed) {
        try {
          const changeStatus = await api.post('/users/banking-details/change-request/activate');
          if (changeStatus.data.has_pending) {
            setPendingChange(changeStatus.data);
            if (changeStatus.data.status === 'pending_verification') {
              setChangeRequestId(changeStatus.data.request_id);
            }
          } else if (changeStatus.data.just_activated) {
            if (changeStatus.data.provider_verified) {
              toast.success('Your new banking details are now active and confirmed.');
            } else {
              toast.warning("Your new banking details were saved, but we couldn't yet confirm them with our payment processor. We'll keep trying automatically.");
            }
            // Reload to reflect the updated details. A full re-fetch of
            // banking-details/status (rather than patching one field) is
            // deliberate here (PR #77 review fix, 2026-10-15): activation
            // just replaced the seller's token, so any previously cached
            // banking_payout_identity_matched belongs to a token that no
            // longer exists — the backend already clears it on activation,
            // and this picks that up instead of carrying the stale value
            // forward via a partial local patch.
            const freshUser = await api.get('/auth/me');
            setSavedBankingDetails(freshUser.data.banking_details);
            const freshStatus = await api.get('/users/banking-details/status');
            setBankingStatus(freshStatus.data);
          }
        } catch {
          // Non-fatal — change status check failed
        }
      }
    } catch (error) {
      if (error.response?.status === 401) navigate('/');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchUserData(); }, [fetchUserData]);

  // ── Initial setup helpers ──────────────────────────────────────────────────

  const handleBankSelect = (bankName) => {
    const bank = SA_BANKS.find(b => b.name === bankName);
    setBankingDetails(prev => ({ ...prev, bank_name: bankName, branch_code: bank?.code || prev.branch_code }));
  };

  const handleSetupSubmit = (e) => {
    e.preventDefault();
    if (!bankingDetails.bank_name || !bankingDetails.account_number || !bankingDetails.branch_code || !bankingDetails.id_number) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (bankingDetails.account_number.length < 8) {
      toast.error('Please enter a valid account number');
      return;
    }
    if (!/^\d{13}$/.test(bankingDetails.id_number)) {
      toast.error('Enter a valid 13-digit South African ID number');
      return;
    }
    setShowConfirmation(true);
  };

  const handleSetupConfirm = async () => {
    setSaving(true);
    try {
      // The backend always distinguishes "saved" from "provider-confirmed" —
      // provider_verified only comes back true when a fresh TradeSafe read
      // (taken after the sync attempt) actually shows the token holding
      // this exact banking and valid. Never assume success from a 200.
      const res = await api.post('/users/banking-details', bankingDetails);
      if (res.data.provider_verified) {
        toast.success('Banking details saved and confirmed with our payment processor');
      } else {
        toast.warning(res.data.message || "Banking details saved, but we couldn't yet confirm them with our payment processor.");
      }
      setSavedBankingDetails({
        bank_name: bankingDetails.bank_name,
        account_holder: bankingDetails.account_holder,
        account_number: bankingDetails.account_number.slice(-4),
        branch_code: bankingDetails.branch_code,
        account_type: bankingDetails.account_type,
        updated_at: new Date().toISOString(),
      });
      setBankingStatus({
        banking_details_completed: true,
        banking_provider_verified: res.data.provider_verified,
        banking_provider_check_detail: res.data.provider_check_detail,
      });
      setShowConfirmation(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save banking details');
    } finally {
      setSaving(false);
    }
  };

  // ── Change flow helpers ────────────────────────────────────────────────────

  const handleChangeBankSelect = (bankName) => {
    const bank = SA_BANKS.find(b => b.name === bankName);
    setChangeDetails(prev => ({ ...prev, bank_name: bankName, branch_code: bank?.code || prev.branch_code }));
  };

  const handleChangeFormSubmit = async (e) => {
    e.preventDefault();
    if (!changeDetails.bank_name || !changeDetails.account_number || !changeDetails.branch_code) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (changeDetails.account_number.length < 8) {
      toast.error('Please enter a valid account number');
      return;
    }
    if (!bankingStatus?.has_id_number && !/^\d{13}$/.test(changeDetails.id_number || '')) {
      toast.error('Enter a valid 13-digit South African ID number');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/users/banking-details/change-request', changeDetails);
      setChangeRequestId(res.data.request_id);
      setChangeOtpChannels({ email: res.data.email_sent_to, sms: res.data.sms_sent_to });
      setOtpValue('');
      setChangeStep('verify');
      toast.success('Verification code sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start change request');
    } finally {
      setSaving(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      const res = await api.post('/users/banking-details/change-request', changeDetails);
      setChangeRequestId(res.data.request_id);
      setChangeOtpChannels({ email: res.data.email_sent_to, sms: res.data.sms_sent_to });
      setOtpValue('');
      toast.success('New verification code sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpValue.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setVerifying(true);
    try {
      const res = await api.post('/users/banking-details/change-request/verify', {
        request_id: changeRequestId,
        otp: otpValue,
      });
      setPendingChange({
        has_pending: true,
        status: 'verified',
        activates_at: res.data.activates_at,
        new_bank_name: changeDetails.bank_name,
        new_account_last4: changeDetails.account_number.slice(-4),
      });
      setChangeStep('cooling');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Incorrect code');
    } finally {
      setVerifying(false);
    }
  };

  const handleCancelChange = async () => {
    setCancelling(true);
    try {
      await api.delete('/users/banking-details/change-request');
      setPendingChange(null);
      setChangeStep(null);
      setChangeRequestId(null);
      setOtpValue('');
      toast.success('Change request cancelled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  // Safe, read-only re-check of the EXISTING payout token against
  // TradeSafe's live state — never creates or replaces a token, never
  // touches bank account details. See POST
  // /users/banking-details/refresh-status (routes/users.py).
  const handleRefreshStatus = async () => {
    setRefreshingStatus(true);
    try {
      const res = await api.post('/users/banking-details/refresh-status');
      if (res.data.provider_unavailable) {
        // Backend deliberately left the stored status untouched when it
        // couldn't reach TradeSafe (PR #77 review fix) — don't describe
        // that as a rejection, and the values it echoes back are just the
        // existing last-known ones, not a new result.
        toast.warning('Our payment processor is temporarily unavailable. Your previous status is unchanged — try again shortly.');
        return;
      }
      setBankingStatus(prev => ({
        ...prev,
        banking_provider_verified: res.data.banking_provider_verified,
        banking_provider_attached: res.data.banking_provider_attached,
        banking_provider_check_detail: res.data.banking_provider_check_detail,
        banking_payout_identity_matched: res.data.banking_payout_identity_matched,
        banking_payout_identity_checked_at: res.data.banking_payout_identity_checked_at,
      }));
      if (res.data.banking_provider_verified || res.data.banking_provider_attached) {
        toast.success('Banking status updated.');
      } else {
        toast.warning("Still couldn't confirm these details with our payment processor.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not check status right now. Please try again.');
    } finally {
      setRefreshingStatus(false);
    }
  };

  // Section 4: a usable UI for POST /users/banking-details/confirm-id-number
  // (PR #76) — never touches banking_details, never triggers the change
  // cooling-off period. The submitted value is cleared from state
  // immediately after the request completes (success or failure) and is
  // never displayed back, logged, or kept anywhere in this component.
  const handleConfirmIdNumber = async (e) => {
    e.preventDefault();
    const value = idNumberInput.trim();
    if (!/^\d{13}$/.test(value)) {
      toast.error('Enter a valid 13-digit South African ID number.');
      return;
    }
    setConfirmingId(true);
    try {
      const res = await api.post('/users/banking-details/confirm-id-number', { id_number: value });
      toast.success(res.data.message || 'ID number saved.');
      setBankingStatus(prev => ({ ...prev, has_id_number: true }));
      setShowIdForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save your ID number. Please try again.');
    } finally {
      setIdNumberInput(''); // never retained, displayed, or logged after submission
      setConfirmingId(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return <DashboardLayout user={null} loading darkMain><div /></DashboardLayout>;
  }

  const surface = { background: V.surface, border: `1px solid ${V.border}`, borderRadius: 4, padding: '20px 24px' };
  const btnPrimary = {
    padding: '10px 20px', borderRadius: 4, border: 'none',
    background: V.accent, color: '#061421', fontFamily: V.sans,
    fontWeight: 700, fontSize: 13, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  };
  const btnGhost = {
    padding: '10px 20px', borderRadius: 4,
    border: `1px solid ${V.border}`, background: 'transparent',
    color: V.text, fontFamily: V.sans, fontSize: 13, cursor: 'pointer',
  };
  const btnDanger = { ...btnGhost, color: '#F0665C', borderColor: '#F0665C' };

  function BackBtn({ onClick, label = 'Back' }) {
    return (
      <button onClick={onClick} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'none', border: 'none', color: V.sub, cursor: 'pointer',
        fontFamily: V.sans, fontSize: 13, marginBottom: 20, padding: 0,
      }}>
        <ArrowLeft size={14} /> {label}
      </button>
    );
  }

  // ── Change flow: cooling-off view ──────────────────────────────────────────
  if (changeStep === 'cooling' || (pendingChange?.status === 'verified' && changeStep === null)) {
    const info = changeStep === 'cooling' ? pendingChange : pendingChange;
    return (
      <DashboardLayout user={user} darkMain>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <BackBtn onClick={() => setChangeStep(null)} label="Back to Banking Details" />

          <h1 style={{ fontFamily: V.sans, fontSize: 22, fontWeight: 700, color: V.text, margin: '0 0 4px' }}>
            Change Request Pending
          </h1>
          <p style={{ color: V.sub, fontSize: 13, marginBottom: 24, fontFamily: V.mono }}>
            24-hour security hold in progress
          </p>

          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 14,
            background: `${V.warn}0F`, border: `1px solid ${V.warn}50`,
            borderRadius: 4, padding: '18px 20px', marginBottom: 16,
          }}>
            <Clock size={20} color={V.warn} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <p style={{ fontWeight: 700, color: V.warn, fontSize: 14, margin: '0 0 4px' }}>
                New details activate on {formatActivatesAt(info?.activates_at)}
              </p>
              <p style={{ color: V.sub, fontSize: 13, margin: 0 }}>
                For your security, banking detail changes require a 24-hour cooling-off period. Your existing details remain active until then.
              </p>
            </div>
          </div>

          <div style={{ ...surface, marginBottom: 16 }}>
            <SectionHead label="Pending New Details" />
            <div style={{ background: V.surface, borderRadius: 4, padding: '0 16px' }}>
              <DetailRow icon={Building2} label="New Bank" value={info?.new_bank_name || changeDetails.bank_name} />
              <DetailRow icon={CreditCard} label="New Account" value={`••••${info?.new_account_last4 || changeDetails.account_number.slice(-4)}`} mono />
            </div>
            <p style={{ fontSize: 11, color: V.dim, marginTop: 12, fontFamily: V.mono }}>
              An email confirmation has been sent to your registered address.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleCancelChange}
              disabled={cancelling}
              style={{ ...btnDanger, flex: 1, justifyContent: 'center', opacity: cancelling ? 0.5 : 1 }}
            >
              <XCircle size={14} /> {cancelling ? 'Cancelling…' : 'Cancel Change Request'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 16 }}>
            <Lock size={14} color={V.dim} style={{ flexShrink: 0, marginTop: 2 }} />
            <p style={{ fontSize: 11, color: V.dim, fontFamily: V.mono, lineHeight: 1.5 }}>
              If you did not request this change, cancel it immediately and contact support at trusttrade.register@gmail.com.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── Change flow: OTP verify view ───────────────────────────────────────────
  if (changeStep === 'verify' || (pendingChange?.status === 'pending_verification' && changeStep === null)) {
    return (
      <DashboardLayout user={user} darkMain>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <BackBtn onClick={() => { setChangeStep('form'); }} label="Edit Details" />

          <h1 style={{ fontFamily: V.sans, fontSize: 22, fontWeight: 700, color: V.text, margin: '0 0 4px' }}>
            Verify Your Identity
          </h1>
          <p style={{ color: V.sub, fontSize: 13, marginBottom: 24, fontFamily: V.mono }}>
            Enter the 6-digit code we sent you
          </p>

          <div style={{ ...surface, marginBottom: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {changeOtpChannels.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Mail size={14} color={V.accent} />
                  <span style={{ fontSize: 13, color: V.sub }}>Code sent to <strong style={{ color: V.text }}>{changeOtpChannels.email}</strong></span>
                </div>
              )}
              {changeOtpChannels.sms && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Phone size={14} color={V.accent} />
                  <span style={{ fontSize: 13, color: V.sub }}>SMS sent to <strong style={{ color: V.text }}>{changeOtpChannels.sms}</strong></span>
                </div>
              )}
              {!changeOtpChannels.email && !changeOtpChannels.sms && (
                <p style={{ fontSize: 13, color: V.sub }}>Check your email for the 6-digit verification code.</p>
              )}
            </div>

            <form onSubmit={handleVerifyOtp}>
              <Field label="Verification Code">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpValue}
                  onChange={e => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  autoFocus
                  style={{
                    ...inputStyle,
                    fontSize: 28, fontFamily: V.mono, letterSpacing: 10,
                    textAlign: 'center', height: 56,
                  }}
                />
              </Field>

              <button
                type="submit"
                disabled={verifying || otpValue.length !== 6}
                style={{
                  ...btnPrimary,
                  width: '100%', justifyContent: 'center', marginBottom: 12,
                  opacity: (verifying || otpValue.length !== 6) ? 0.5 : 1,
                  cursor: (verifying || otpValue.length !== 6) ? 'not-allowed' : 'pointer',
                  boxShadow: `0 0 16px ${V.accent}40`,
                }}
              >
                <ShieldCheck size={15} />
                {verifying ? 'Verifying…' : 'Confirm Code'}
              </button>
            </form>

            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 12, color: V.dim, marginBottom: 8 }}>Didn't receive a code? Codes expire in 10 minutes.</p>
              <button
                onClick={handleResendOtp}
                disabled={resending}
                style={{ background: 'none', border: 'none', color: V.accent, fontSize: 13, cursor: 'pointer', opacity: resending ? 0.5 : 1 }}
              >
                {resending ? 'Sending…' : 'Resend code'}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <AlertCircle size={14} color={V.dim} style={{ flexShrink: 0, marginTop: 2 }} />
            <p style={{ fontSize: 11, color: V.dim, fontFamily: V.mono, lineHeight: 1.5 }}>
              After verification, a 24-hour security hold applies before your new details activate.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── Change flow: new details form ──────────────────────────────────────────
  if (changeStep === 'form') {
    return (
      <DashboardLayout user={user} darkMain>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <BackBtn onClick={() => setChangeStep(null)} label="Cancel Change" />

          <h1 style={{ fontFamily: V.sans, fontSize: 22, fontWeight: 700, color: V.text, margin: '0 0 4px' }}>
            Change Banking Details
          </h1>
          <p style={{ color: V.sub, fontSize: 13, marginBottom: 24, fontFamily: V.mono }}>
            Enter your new bank account details
          </p>

          <div style={{
            display: 'flex', gap: 12,
            background: `${V.warn}0F`, border: `1px solid ${V.warn}40`,
            borderRadius: 4, padding: '14px 18px', marginBottom: 20,
          }}>
            <AlertCircle size={16} color={V.warn} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <p style={{ fontWeight: 600, color: V.warn, fontSize: 13, margin: '0 0 4px' }}>
                Verification required
              </p>
              <p style={{ color: V.sub, fontSize: 12, margin: 0 }}>
                A code will be sent to your email and phone. New details activate after a 24-hour security hold.
              </p>
            </div>
          </div>

          <div style={{ ...surface, marginBottom: 16 }}>
            <SectionHead label="New Bank Account" />
            <form onSubmit={handleChangeFormSubmit}>

              <Field label="Bank Name *" icon={Building2}>
                <select
                  value={changeDetails.bank_name}
                  onChange={e => handleChangeBankSelect(e.target.value)}
                  required
                  style={{ ...inputStyle, height: 38, cursor: 'pointer' }}
                >
                  <option value="">Select your bank</option>
                  {SA_BANKS.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
                </select>
              </Field>

              <Field
                label="Account Holder Name *"
                icon={User}
                hint="Your own name (or your registered business name) exactly as it appears on this bank account — not TrustTrade."
              >
                <input
                  value={changeDetails.account_holder}
                  onChange={e => setChangeDetails(prev => ({ ...prev, account_holder: e.target.value }))}
                  placeholder="Full name as it appears on account"
                  required
                  style={inputStyle}
                />
              </Field>

              <Field label="Account Number *" icon={CreditCard}>
                <input
                  type="text"
                  inputMode="numeric"
                  value={changeDetails.account_number}
                  onChange={e => setChangeDetails(prev => ({ ...prev, account_number: e.target.value.replace(/\D/g, '') }))}
                  placeholder="Your new bank account number"
                  required
                  style={{ ...inputStyle, fontFamily: V.mono }}
                />
              </Field>

              <Field
                label="Branch Code *"
                icon={Hash}
                hint={changeDetails.bank_name && changeDetails.bank_name !== 'Other'
                  ? `Universal branch code auto-filled for ${changeDetails.bank_name}`
                  : undefined}
              >
                <input
                  value={changeDetails.branch_code}
                  onChange={e => setChangeDetails(prev => ({ ...prev, branch_code: e.target.value }))}
                  placeholder="6-digit branch code"
                  required
                  style={{ ...inputStyle, fontFamily: V.mono }}
                />
              </Field>

              {!bankingStatus?.has_id_number && (
                <Field
                  label="South African ID number *"
                  icon={Hash}
                  hint="Required by the payment processor before a payout account can be saved. This is stored on your TrustTrade profile."
                >
                  <input
                    inputMode="numeric"
                    autoComplete="off"
                    value={changeDetails.id_number}
                    onChange={e => setChangeDetails(prev => ({ ...prev, id_number: e.target.value.replace(/\D/g, '').slice(0, 13) }))}
                    placeholder="13-digit ID number"
                    required
                    style={{ ...inputStyle, fontFamily: V.mono }}
                  />
                </Field>
              )}

              <Field label="Account Type *">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[{ v: 'savings', l: 'Savings' }, { v: 'current', l: 'Current / Cheque' }].map(({ v, l }) => (
                    <label key={v} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', borderRadius: 4, cursor: 'pointer',
                      border: `1px solid ${changeDetails.account_type === v ? V.accent : V.border}`,
                      background: changeDetails.account_type === v ? `${V.accent}0A` : 'transparent',
                    }}>
                      <input
                        type="radio"
                        name="change_account_type"
                        value={v}
                        checked={changeDetails.account_type === v}
                        onChange={e => setChangeDetails(prev => ({ ...prev, account_type: e.target.value }))}
                        style={{ accentColor: V.accent }}
                      />
                      <span style={{ fontSize: 13, color: V.text, fontWeight: 500 }}>{l}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <button
                type="submit"
                disabled={saving}
                style={{
                  ...btnPrimary,
                  width: '100%', justifyContent: 'center', marginTop: 4,
                  opacity: saving ? 0.5 : 1, cursor: saving ? 'not-allowed' : 'pointer',
                  boxShadow: saving ? 'none' : `0 0 16px ${V.accent}40`,
                }}
              >
                <ShieldCheck size={15} />
                {saving ? 'Sending Code…' : 'Send Verification Code'}
              </button>
            </form>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── Saved state (read-only with optional pending-change notice) ────────────
  if (bankingStatus?.banking_details_completed && savedBankingDetails) {
    return (
      <DashboardLayout user={user} darkMain>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <BackBtn onClick={() => navigate('/dashboard')} label="Back to Dashboard" />

          <h1 style={{ fontFamily: V.sans, fontSize: 22, fontWeight: 700, color: V.text, margin: '0 0 4px' }}>
            Banking Details
          </h1>
          <p style={{ color: V.sub, fontSize: 13, marginBottom: 24, fontFamily: V.mono }}>Your payout account</p>

          {/* Status banner — "saved" (a form was submitted) and
              "provider-confirmed" are never the same claim, and "confirmed"
              itself has two honest tiers: exact-digit verified vs. attached-
              but-masked (still a real, usable account — see
              getBankingStatusPresentation above for why this is no longer
              collapsed into one vague "pending" state). */}
          {(() => {
            const presentation = getBankingStatusPresentation(bankingStatus);
            const tone = presentation.tone === 'success' ? V.success : V.warn;
            return (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                background: `${tone}10`, border: `1px solid ${tone}40`,
                borderRadius: 4, padding: '14px 18px', marginBottom: 16,
              }}>
                {presentation.tone === 'success'
                  ? <CheckCircle size={18} color={tone} style={{ flexShrink: 0, marginTop: 1 }} />
                  : <AlertCircle size={18} color={tone} style={{ flexShrink: 0, marginTop: 1 }} />}
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, color: tone, fontSize: 13, margin: 0 }}>
                    {presentation.title}
                  </p>
                  <p style={{ color: V.sub, fontSize: 12, margin: '2px 0 0', lineHeight: 1.5 }}>
                    {presentation.message}
                  </p>
                  {presentation.tone !== 'success' && (
                    <button
                      type="button"
                      onClick={handleRefreshStatus}
                      disabled={refreshingStatus}
                      style={{
                        marginTop: 10, padding: '6px 12px', borderRadius: 4,
                        border: `1px solid ${V.border}`, background: 'transparent',
                        color: V.text, fontSize: 12, fontFamily: V.sans, fontWeight: 600,
                        cursor: refreshingStatus ? 'not-allowed' : 'pointer', opacity: refreshingStatus ? 0.6 : 1,
                      }}
                    >
                      {refreshingStatus ? 'Checking…' : 'Refresh status'}
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Pending change notice. Section 1 fix (2026-10-14): the
              "blocked_active_transactions" status — the server holds
              activation while any transaction still has funds/payout in
              flight on the CURRENT token, see tradesafe_service.py's
              count_seller_active_transactions — used to fall through to the
              generic "Change pending — activates <nothing>" label with the
              server's own explanatory message never shown at all. Now
              surfaced explicitly, verbatim (no sensitive banking data in
              that message — it never names an amount, account, or deal). */}
          {pendingChange?.has_pending && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              background: `${V.warn}0F`, border: `1px solid ${V.warn}50`,
              borderRadius: 4, padding: '14px 18px', marginBottom: 16,
            }}>
              <Clock size={16} color={V.warn} style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, color: V.warn, fontSize: 13, margin: '0 0 4px' }}>
                  {pendingChange.status === 'blocked_active_transactions'
                    ? 'Change on hold — active transaction in progress'
                    : pendingChange.status === 'activating'
                      // PR #77 review fix (2026-10-15): a request this
                      // close to activating (cooling-off already elapsed,
                      // genuinely being processed right now) must say so
                      // accurately rather than showing a stale "activates
                      // <already-past time>" label.
                      ? 'Activating now'
                      : pendingChange.status === 'activation_needs_support'
                        // PR #77 review fix (2026-10-15, fail-closed pass):
                        // a request stuck in "activating" past the support-
                        // review threshold is deliberately never retried or
                        // reclaimed automatically — a human resolves it.
                        ? 'Banking change requires support review'
                        : pendingChange.status === 'activation_failed'
                          ? 'Action required — change could not be confirmed'
                          : pendingChange.status === 'pending_verification'
                            ? 'Change request awaiting verification'
                            : `Change pending — activates ${formatActivatesAt(pendingChange.activates_at)}`}
                </p>
                <p style={{ color: V.sub, fontSize: 12, margin: '0 0 10px', lineHeight: 1.5 }}>
                  {(pendingChange.status === 'blocked_active_transactions'
                    || pendingChange.status === 'activating'
                    || pendingChange.status === 'activation_needs_support'
                    || pendingChange.status === 'activation_failed')
                    ? (pendingChange.message || 'Your existing banking details are still in effect.')
                    : (pendingChange.new_bank_name && (
                      <>
                        New bank: <strong style={{ color: V.text }}>{pendingChange.new_bank_name}</strong>
                        {pendingChange.new_account_last4 && ` ••••${pendingChange.new_account_last4}`}
                      </>
                    ))}
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  {pendingChange.status === 'pending_verification' && (
                    <button
                      onClick={() => setChangeStep('verify')}
                      style={{ ...btnPrimary, fontSize: 12, padding: '6px 14px' }}
                    >
                      Enter Code
                    </button>
                  )}
                  {pendingChange.status === 'verified' && (
                    <button
                      onClick={() => setChangeStep('cooling')}
                      style={{ ...btnGhost, fontSize: 12, padding: '6px 14px' }}
                    >
                      View Status
                    </button>
                  )}
                  {/* Cancelling is guaranteed to fail (409) while the
                      backend still sees this as "activating" — never show
                      an action that can't succeed; support resolves it. */}
                  {pendingChange.status !== 'activation_needs_support' && (
                    <button
                      onClick={handleCancelChange}
                      disabled={cancelling}
                      style={{ ...btnDanger, fontSize: 12, padding: '6px 14px', opacity: cancelling ? 0.5 : 1 }}
                    >
                      Cancel Change
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Current saved details */}
          <div style={{ ...surface, marginBottom: 16 }}>
            <SectionHead label="Current Banking Details" />
            <div style={{ background: V.surface, borderRadius: 4, padding: '0 16px' }}>
              <DetailRow icon={Building2} label="Bank Name"       value={savedBankingDetails.bank_name} />
              <DetailRow icon={User}      label="Account Holder"  value={savedBankingDetails.account_holder} />
              <DetailRow icon={CreditCard} label="Account Number" value={`••••••${savedBankingDetails.account_number}`} mono />
              <DetailRow icon={Hash}      label="Branch Code"     value={savedBankingDetails.branch_code} mono />
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
                <span style={{ color: V.sub, fontSize: 13 }}>Account Type</span>
                <span style={{ fontWeight: 600, color: V.text, fontSize: 13, textTransform: 'capitalize' }}>
                  {savedBankingDetails.account_type}
                </span>
              </div>
            </div>
            {savedBankingDetails.updated_at && (
              <p style={{ fontSize: 11, color: V.dim, marginTop: 12, fontFamily: V.mono }}>
                Last updated: {new Date(savedBankingDetails.updated_at).toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
          </div>

          {/* Change button — only show if no pending request */}
          {!pendingChange?.has_pending && (
            <button
              onClick={() => setChangeStep('form')}
              style={{ ...btnPrimary, marginBottom: 16 }}
            >
              <CreditCard size={14} /> Change Banking Details
            </button>
          )}

          {/* ID reconfirmation (Section 4, 2026-10-14) — never touches
              banking_details, never triggers the 24h change cooling-off
              period. Separate axis entirely: this is identity data used to
              recover a payout token's provider-side identity, not a bank
              account change. */}
          <div style={{ ...surface, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Hash size={15} color={bankingStatus?.has_id_number ? V.sub : V.warn} />
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: V.text, margin: 0 }}>
                    {bankingStatus?.has_id_number ? 'ID number on file' : 'ID number needed'}
                  </p>
                  <p style={{ fontSize: 11, color: V.dim, margin: '2px 0 0' }}>
                    {bankingStatus?.has_id_number
                      ? 'You can re-confirm it if your payout details ever need to be recreated.'
                      : "Needed if your payout account ever has to be recreated — e.g. after a profile name or email change."}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowIdForm(s => !s)}
                style={{ ...btnGhost, fontSize: 12, padding: '6px 14px', whiteSpace: 'nowrap' }}
              >
                {showIdForm ? 'Cancel' : bankingStatus?.has_id_number ? 'Re-confirm ID' : 'Confirm ID'}
              </button>
            </div>

            {showIdForm && (
              <form onSubmit={handleConfirmIdNumber} style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${V.border}` }}>
                <Field label="South African ID Number *" icon={Hash} hint="13 digits. Never shown or logged again after you submit it.">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={13}
                    value={idNumberInput}
                    onChange={e => setIdNumberInput(e.target.value.replace(/\D/g, '').slice(0, 13))}
                    placeholder="13-digit ID number"
                    required
                    style={inputStyle}
                    autoComplete="off"
                  />
                </Field>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 14 }}>
                  <AlertCircle size={13} color={V.dim} style={{ flexShrink: 0, marginTop: 2 }} />
                  <p style={{ fontSize: 11, color: V.dim, lineHeight: 1.5, margin: 0 }}>
                    Saving this does not mean our payment processor has verified it — that only happens automatically
                    the next time it's needed for a payout, and we'll never show the full number back to you again.
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={confirmingId || idNumberInput.length !== 13}
                  style={{
                    ...btnPrimary,
                    opacity: (confirmingId || idNumberInput.length !== 13) ? 0.5 : 1,
                    cursor: (confirmingId || idNumberInput.length !== 13) ? 'not-allowed' : 'pointer',
                  }}
                >
                  <ShieldCheck size={14} /> {confirmingId ? 'Saving…' : 'Save ID Number'}
                </button>
              </form>
            )}
          </div>

          {/* Security note */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Lock size={14} color={V.dim} style={{ flexShrink: 0, marginTop: 2 }} />
            <p style={{ fontSize: 11, color: V.dim, fontFamily: V.mono, lineHeight: 1.5 }}>
              Your full banking details are encrypted and stored securely with our payment processor.
              Changes require email + SMS verification and a 24-hour cooling-off period.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── Saved but no detail object loaded ──────────────────────────────────────
  if (bankingStatus?.banking_details_completed) {
    const presentation = getBankingStatusPresentation(bankingStatus);
    const tone = presentation.tone === 'success' ? V.success : V.warn;
    return (
      <DashboardLayout user={user} darkMain>
        <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center', paddingTop: 60 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: `${tone}18`, border: `1px solid ${tone}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            {presentation.tone === 'success'
              ? <CheckCircle size={28} color={tone} />
              : <AlertCircle size={28} color={tone} />}
          </div>
          <h2 style={{ fontFamily: V.sans, fontSize: 20, fontWeight: 700, color: V.text, marginBottom: 8 }}>
            {presentation.title}
          </h2>
          <p style={{ color: V.sub, fontSize: 13, marginBottom: 24, lineHeight: 1.5 }}>
            {presentation.message}
          </p>
          {presentation.tone !== 'success' && (
            <button
              type="button"
              onClick={handleRefreshStatus}
              disabled={refreshingStatus}
              style={{ ...btnGhost, marginBottom: 12, opacity: refreshingStatus ? 0.6 : 1, cursor: refreshingStatus ? 'not-allowed' : 'pointer' }}
            >
              {refreshingStatus ? 'Checking…' : 'Refresh status'}
            </button>
          )}
          <button onClick={() => navigate('/dashboard')} style={btnPrimary}>
            Back to Dashboard
          </button>
        </div>
      </DashboardLayout>
    );
  }

  // ── Initial setup: confirm step ────────────────────────────────────────────
  if (showConfirmation) {
    return (
      <DashboardLayout user={user} darkMain>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <BackBtn onClick={() => setShowConfirmation(false)} label="Edit Details" />

          <h1 style={{ fontFamily: V.sans, fontSize: 22, fontWeight: 700, color: V.text, margin: '0 0 4px' }}>
            Confirm Banking Details
          </h1>
          <p style={{ color: V.sub, fontSize: 13, marginBottom: 24, fontFamily: V.mono }}>Please verify your information</p>

          <div style={{ ...surface, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
              <AlertCircle size={16} color={V.warn} style={{ flexShrink: 0, marginTop: 2 }} />
              <p style={{ color: V.sub, fontSize: 13 }}>
                Please verify all details are correct before submitting. Incorrect details may delay your payouts.
              </p>
            </div>

            <div style={{ background: V.surface, borderRadius: 4, padding: '0 16px', marginBottom: 20 }}>
              <DetailRow label="Bank Name"      value={bankingDetails.bank_name} />
              <DetailRow label="Account Holder" value={bankingDetails.account_holder} />
              <DetailRow label="Account Number" value={bankingDetails.account_number} mono />
              <DetailRow label="Branch Code"    value={bankingDetails.branch_code} mono />
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
                <span style={{ color: V.sub, fontSize: 13 }}>Account Type</span>
                <span style={{ fontWeight: 600, color: V.text, fontSize: 13, textTransform: 'capitalize' }}>
                  {bankingDetails.account_type}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowConfirmation(false)} disabled={saving} style={{ ...btnGhost, flex: 1 }}>
                Edit Details
              </button>
              <button
                onClick={handleSetupConfirm}
                disabled={saving}
                style={{
                  ...btnPrimary, flex: 1, justifyContent: 'center',
                  opacity: saving ? 0.5 : 1, cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? 'Saving…' : <><CheckCircle size={14} /> Confirm &amp; Submit</>}
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── Initial setup: entry form ──────────────────────────────────────────────
  return (
    <DashboardLayout user={user} darkMain>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <BackBtn onClick={() => navigate('/dashboard')} label="Back" />

        <h1 style={{ fontFamily: V.sans, fontSize: 22, fontWeight: 700, color: V.text, margin: '0 0 4px' }}>
          Banking Details
        </h1>
        <p style={{ color: V.sub, fontSize: 13, marginBottom: 24, fontFamily: V.mono }}>
          Add your bank account to receive payouts
        </p>

        <div style={{
          display: 'flex', gap: 12,
          background: '#FFF1F1', border: '1px solid #E8B6B6',
          borderRadius: 4, padding: '14px 18px', marginBottom: 20,
        }}>
          <AlertCircle size={16} color="#F0665C" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <p style={{ fontWeight: 700, color: '#F0665C', fontSize: 13, margin: '0 0 4px' }}>Banking details required</p>
            <p style={{ color: V.sub, fontSize: 12, margin: 0 }}>
              You cannot create or accept deals as a seller until your banking details are saved.
              Details are stored to enable your payouts and transmitted securely to our payment processor, TradeSafe.
            </p>
          </div>
        </div>

        <div style={{ ...surface, marginBottom: 16 }}>
          <SectionHead label="Bank Account" />
          <form onSubmit={handleSetupSubmit}>

            <Field label="Bank Name *" icon={Building2}>
              <select
                value={bankingDetails.bank_name}
                onChange={e => handleBankSelect(e.target.value)}
                required
                style={{ ...inputStyle, height: 38, cursor: 'pointer' }}
              >
                <option value="">Select your bank</option>
                {SA_BANKS.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
              </select>
            </Field>

            <Field
              label="Account Holder Name *"
              icon={User}
              hint="Your own name (or your registered business name) exactly as it appears on this bank account — not TrustTrade."
            >
              <input
                value={bankingDetails.account_holder}
                onChange={e => setBankingDetails(prev => ({ ...prev, account_holder: e.target.value }))}
                placeholder="Full name as it appears on account"
                required
                style={inputStyle}
              />
            </Field>

            <Field label="Account Number *" icon={CreditCard}>
              <input
                type="text"
                inputMode="numeric"
                value={bankingDetails.account_number}
                onChange={e => setBankingDetails(prev => ({ ...prev, account_number: e.target.value.replace(/\D/g, '') }))}
                placeholder="Your bank account number"
                required
                style={{ ...inputStyle, fontFamily: V.mono }}
              />
            </Field>

            <Field
              label="Branch Code *"
              icon={Hash}
              hint={bankingDetails.bank_name && bankingDetails.bank_name !== 'Other'
                ? `Universal branch code auto-filled for ${bankingDetails.bank_name}`
                : undefined}
            >
              <input
                value={bankingDetails.branch_code}
                onChange={e => setBankingDetails(prev => ({ ...prev, branch_code: e.target.value }))}
                placeholder="6-digit branch code"
                required
                style={{ ...inputStyle, fontFamily: V.mono }}
              />
            </Field>

            <Field
              label="South African ID number *"
              icon={Hash}
              hint="Required before payout details can be saved. Use the 13-digit number on your ID card."
            >
              <input
                inputMode="numeric"
                autoComplete="off"
                value={bankingDetails.id_number}
                onChange={e => setBankingDetails(prev => ({ ...prev, id_number: e.target.value.replace(/\D/g, '').slice(0, 13) }))}
                placeholder="13-digit ID number"
                required
                style={{ ...inputStyle, fontFamily: V.mono }}
              />
            </Field>

            <Field label="Account Type *">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[{ v: 'savings', l: 'Savings' }, { v: 'current', l: 'Current / Cheque' }].map(({ v, l }) => (
                  <label key={v} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', borderRadius: 4, cursor: 'pointer',
                    border: `1px solid ${bankingDetails.account_type === v ? V.accent : V.border}`,
                    background: bankingDetails.account_type === v ? `${V.accent}0A` : 'transparent',
                    transition: 'border-color 0.15s',
                  }}>
                    <input
                      type="radio"
                      name="account_type"
                      value={v}
                      checked={bankingDetails.account_type === v}
                      onChange={e => setBankingDetails(prev => ({ ...prev, account_type: e.target.value }))}
                      style={{ accentColor: V.accent }}
                    />
                    <span style={{ fontSize: 13, color: V.text, fontWeight: 500 }}>{l}</span>
                  </label>
                ))}
              </div>
            </Field>

            <button
              type="submit"
              disabled={saving}
              style={{
                ...btnPrimary,
                width: '100%', justifyContent: 'center', marginTop: 4,
                opacity: saving ? 0.5 : 1, cursor: saving ? 'not-allowed' : 'pointer',
                boxShadow: saving ? 'none' : `0 0 16px ${V.accent}40`,
              }}
            >
              <ShieldCheck size={15} />
              {saving ? 'Saving Securely…' : 'Save Banking Details'}
            </button>
          </form>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Lock size={14} color={V.dim} style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={{ fontSize: 11, color: V.dim, fontFamily: V.mono, lineHeight: 1.5 }}>
            Your banking details are stored to enable payouts and transmitted securely to our
            payment processor, TradeSafe, for payout processing.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default BankingSettings;
